import React, { useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, Clock, CheckCircle2, ChevronRight } from "lucide-react";
import { ClientsContext, ClientGroupsContext } from "@/App";
import { STAGE_COLORS, STAGE_PATHS, fmtAge, fmtDateShort, parseIsoOrDash } from "./utils";

/**
 * AttentionLightbox — quick drill-in for Dashboard's Blocked / Overdue cards.
 * Self-contained: takes the full items list + the mode and renders a sortable
 * list. Clicking a row navigates to the matching stage page.
 */
export default function AttentionLightbox({ open, onOpenChange, mode, items }) {
  const navigate = useNavigate();
  const { clients } = useContext(ClientsContext);
  const { clientGroups } = useContext(ClientGroupsContext);

  const clientLogoMap = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      if (c.logo_svg) { map[c.name] = c.logo_svg; return; }
      const group = (clientGroups || []).find(g => (g.client_ids || []).includes(c.id));
      if (group?.logo_svg) map[c.name] = group.logo_svg;
    });
    return map;
  }, [clients, clientGroups]);

  const rows = useMemo(() => {
    if (!open) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (mode === "blocked") {
      return items
        .filter(i => i.is_blocked && i.stage !== "Completed")
        .map(i => {
          const secs = i.blocked_at ? Math.max(0, (Date.now() - new Date(i.blocked_at).getTime()) / 1000) : -1;
          return {
            item: i,
            ageSecs: secs,
            ageLabel: i.blocked_at ? fmtAge(i.blocked_at) : "—",
            sinceLabel: i.blocked_at ? fmtDateShort(i.blocked_at) : "",
            actor: i.blocked_by || "",
            actorRole: i.blocked_by_role || "",
            reason: i.block_reason || "",
            reasonCode: i.block_reason_code || "",
          };
        })
        .sort((a, b) => b.ageSecs - a.ageSecs);
    }
    // overdue
    return items
      .filter(i => i.stage !== "Completed")
      .map(i => {
        const due = parseIsoOrDash(i.dispatch_due_date || i.due_date);
        if (!due || due >= today) return null;
        const days = Math.floor((today - due) / 86400000);
        return {
          item: i,
          ageSecs: days * 86400,
          ageLabel: `${days}d`,
          sinceLabel: fmtDateShort(i.dispatch_due_date || i.due_date),
          dueRaw: i.dispatch_due_date || i.due_date,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.ageSecs - a.ageSecs);
  }, [open, mode, items]);

  const cfg = mode === "blocked"
    ? { title: "Blocked Items", subtitle: "Sorted by longest-blocked first", accent: "#ef4444", icon: ShieldAlert, empty: "Nothing is blocked right now. Pipeline is clean." }
    : { title: "Overdue Items", subtitle: "Dispatch / due date has passed — sorted most-overdue first", accent: "#f97316", icon: Clock, empty: "No overdue items. Every active job is on schedule." };

  const Icon = cfg.icon;

  const handleRowClick = (item) => {
    onOpenChange(false);
    const path = STAGE_PATHS[item.stage];
    if (path) navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[760px] max-w-[95vw] max-h-[88vh] overflow-hidden flex flex-col p-0"
        data-testid={`attention-lightbox-${mode}`}
      >
        <div className="h-[3px] w-full" style={{ background: `linear-gradient(to right, ${cfg.accent}, ${cfg.accent}66)` }} />

        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[hsl(var(--border))]">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon className="w-5 h-5" style={{ color: cfg.accent }} />
            <span>{cfg.title}</span>
            <span className="font-mono-data text-xs px-2 py-0.5 rounded border" style={{ color: cfg.accent, borderColor: cfg.accent + "55", backgroundColor: cfg.accent + "14" }}>
              {rows.length}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[hsl(var(--muted-foreground))]">
            {cfg.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-3 py-2" data-testid={`attention-list-${mode}`}>
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
              <CheckCircle2 className="w-10 h-10 mb-2 text-emerald-500 opacity-70" />
              <p className="text-sm font-medium">{cfg.empty}</p>
            </div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--border))]/60">
              {rows.map(r => <AttentionRow key={r.item.id} row={r} mode={mode} accent={cfg.accent} clientLogoMap={clientLogoMap} onClick={() => handleRowClick(r.item)} />)}
            </ul>
          )}
        </div>

        <div className="px-6 py-2.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 flex items-center justify-between">
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
            Tap any row to open its stage view.
          </span>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid={`attention-close-${mode}`}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttentionRow({ row, mode, accent, onClick, clientLogoMap }) {
  const stage = row.item.stage || "";
  const stageColor = STAGE_COLORS[stage]?.color || "#94a3b8";
  const isRework = row.item.is_rework;
  const clientName = row.item.client || "";
  const clientLogoSvg = clientLogoMap?.[clientName] || "";
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left py-2.5 px-3 rounded-md hover:bg-[hsl(var(--muted))]/50 focus:outline-none focus-visible:bg-[hsl(var(--muted))]/60 transition-colors flex items-center gap-3"
        data-testid={`attention-row-${row.item.id}`}
      >
        <div className="w-[3px] self-stretch rounded-full shrink-0" style={{ backgroundColor: stageColor }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-data text-[11px] font-bold text-[hsl(var(--foreground))]">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>
              {row.item.po_number}
              {row.item.line_number && (
                <>
                  <span className="opacity-40 mx-1">·</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">LINE</span>
                  {row.item.line_number}
                </>
              )}
            </span>
            {isRework && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded bg-teal-500/15 text-teal-500 border border-teal-500/30">Rework</span>
            )}
          </div>
          <div className="text-sm font-medium text-[hsl(var(--foreground))] truncate mt-0.5">
            {row.item.item_description}
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold" style={{ color: stageColor }}>{stage}</span>
            <span className="opacity-50">·</span>
            <span className="inline-flex items-center gap-1">
              {clientLogoSvg ? (
                <span
                  className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                  style={{ width: 12, height: 12 }}
                  dangerouslySetInnerHTML={{ __html: clientLogoSvg }}
                />
              ) : (
                <span
                  className="inline-flex items-center justify-center shrink-0 rounded-full text-[5px] font-bold leading-none"
                  style={{ width: 12, height: 12, color: "hsl(var(--muted-foreground))", backgroundColor: "hsl(var(--muted-foreground) / 0.15)" }}
                >
                  {clientName.split(" ").slice(0, 2).map(w => w[0]).join("")}
                </span>
              )}
              {clientName}
            </span>
            {mode === "blocked" && row.reasonCode && (
              <>
                <span className="opacity-50">·</span>
                <span className="font-mono-data font-bold px-1 py-0 rounded bg-red-500/15 text-red-500 border border-red-500/30 text-[10px]">{row.reasonCode}</span>
              </>
            )}
            {mode === "blocked" && row.reason && (
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic truncate" title={row.reason}>
                — {row.reason}
              </span>
            )}
            {mode === "overdue" && row.dueRaw && (
              <>
                <span className="opacity-50">·</span>
                <span>Due {fmtDateShort(row.dueRaw)}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right shrink-0 pl-3 min-w-[110px]">
          <div className="font-mono-data font-bold text-sm" style={{ color: accent }}>{row.ageLabel}</div>
          {row.sinceLabel && (
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
              {mode === "blocked" ? "since " : "due "}{row.sinceLabel}
            </div>
          )}
          {mode === "blocked" && row.actor && (
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5 truncate max-w-[110px]" title={`by ${row.actor}${row.actorRole ? ` (${row.actorRole})` : ""}`}>
              by {row.actor}
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]/40 shrink-0" />
      </button>
    </li>
  );
}
